import { computed, ref } from 'vue';
import SalesPropertyLookupsAPI from 'dashboard/api/salesPropertyLookups';

const MIN_CODE_LENGTH = 6;

export function useSalesPropertyLookup({ t }) {
  const code = ref('');
  const property = ref(null);
  const listing = ref(null);
  const source = ref('');
  const loading = ref(false);
  const error = ref('');
  let requestSequence = 0;

  const canConfirm = computed(() => Boolean(property.value && listing.value));

  const reset = () => {
    requestSequence += 1;
    property.value = null;
    listing.value = null;
    source.value = '';
    error.value = '';
    loading.value = false;
  };

  const messageFor = status => {
    if (status === 'invalid') return t('SALES.CREATE.INVALID_CODE');
    if (status === 'no_sale_listing') {
      return t('SALES.CREATE.NO_SALE_LISTING');
    }
    if (status === 'not_configured') {
      return t('SALES.CREATE.MIDAS_NOT_CONFIGURED');
    }

    return t('SALES.CREATE.SEARCH_ERROR');
  };

  const search = async value => {
    const normalizedCode = String(value || '').trim();
    code.value = normalizedCode;
    if (normalizedCode.length < MIN_CODE_LENGTH) {
      reset();
      code.value = normalizedCode;
      return null;
    }

    requestSequence += 1;
    const sequence = requestSequence;
    loading.value = true;
    error.value = '';
    try {
      const response = await SalesPropertyLookupsAPI.lookup(normalizedCode);
      if (sequence !== requestSequence) return null;

      const payload = response?.data || {};
      if (payload.status !== 'success') {
        reset();
        code.value = normalizedCode;
        error.value = messageFor(payload.status);
        return null;
      }

      property.value = payload.property;
      listing.value = payload.listing;
      source.value = payload.source || '';
      return payload.property;
    } catch (requestError) {
      if (sequence !== requestSequence) return null;
      error.value =
        requestError?.response?.data?.message || t('SALES.CREATE.SEARCH_ERROR');
      return null;
    } finally {
      if (sequence === requestSequence) loading.value = false;
    }
  };

  return {
    canConfirm,
    code,
    error,
    listing,
    loading,
    property,
    reset,
    search,
    source,
  };
}
